import { useState } from 'react'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { validate as validateEmail } from 'isemail'

import logo from '../../assets/images/logo.png'
import Button from '../Button/Button'

const Page = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  padding: 40px 20px;
  background-color: var(--color-bg-main);
  color: var(--color-text-primary);
`

const Logo = styled.img`
  width: 180px;
  margin-bottom: 32px;
`

const Box = styled.div`
  width: 100%;
  max-width: 540px;
  background-color: var(--color-bg-card);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 32px;
`

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--color-text-primary);
`

const Lead = styled.p`
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-text-primary);
`

const Input = styled.input`
  border: 2px solid var(--color-text-secondary);
  background-color: var(--color-bg-input);
  padding: 12px 16px;
  border-radius: 8px;
  color: var(--color-input-text, #4b5882);
  font-size: 14px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(151, 202, 111, 0.2);
    background-color: #fff;
  }

  &:hover {
    border-color: var(--color-primary);
  }
`

const ErrorMsg = styled.span`
  color: var(--color-error);
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  font-family: 'Roboto', Arial, sans-serif;
`

const Actions = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
`

const RESPONSE_TIME_OPTIONS = [
  { label: 'Within 15 minutes 24/7', value: 'within15Minutes247' },
  { label: 'Within 1 Business Day', value: 'within1BusinessDay' },
]

const WHY_RESET_OPTIONS = [
  { label: 'New Phone', value: 'New Phone' },
  { label: 'Never Set Up', value: 'Never Set Up' },
  { label: 'Not Working', value: 'Not Working' },
  { label: 'Other', value: 'Other' },
]

const selectTypography = {
  color: 'var(--color-text-secondary, #a5acc2)',
  fontSize: '14px',
  fontWeight: 500,
}

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    height: '46px',
    padding: '0 8px',
    border: state.isFocused
      ? '2px solid var(--color-primary)'
      : '2px solid var(--color-text-secondary, #a5acc2)',
    borderRadius: '8px',
    backgroundColor: 'var(--color-bg-input)',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(151, 202, 111, 0.2)' : 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--color-primary)' },
  }),
  singleValue: (provided) => ({ ...provided, ...selectTypography }),
  placeholder: (provided) => ({ ...provided, ...selectTypography }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({ ...provided, color: 'var(--color-text-secondary)' }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-bg-card, #2a2a2a)',
    border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
    borderRadius: '4px',
  }),
  option: (provided, state) => ({
    ...provided,
    ...selectTypography,
    backgroundColor: state.isFocused
      ? 'var(--color-bg-sidebar, #3a3a3a)'
      : 'var(--color-bg-card, #2a2a2a)',
    cursor: 'pointer',
  }),
}

const MfaResetForm = () => {
  const [form, setForm] = useState({
    name: '',
    companyName: '',
    email: '',
    responseTime: null,
    whyReset: null,
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }))
  }

  const handleResponseTimeChange = (option) => {
    setForm((prev) => ({ ...prev, responseTime: option }))
    if (errors.responseTime) setErrors((prev) => ({ ...prev, responseTime: undefined }))
  }

  const handleWhyResetChange = (option) => {
    setForm((prev) => ({ ...prev, whyReset: option }))
    if (errors.whyReset) setErrors((prev) => ({ ...prev, whyReset: undefined }))
  }

  const validate = () => {
    const next = {}
    if (!form.name.trim()) next.name = 'Please enter your name'
    if (!form.companyName.trim()) next.companyName = 'Please enter your company name'
    if (!form.email.trim() || !validateEmail(form.email.trim())) next.email = 'Please provide a valid email'
    if (!form.responseTime) next.responseTime = 'Please choose a response time'
    if (!form.whyReset) next.whyReset = 'Please choose a reason'
    return next
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const validation = validate()
    setErrors(validation)
    if (Object.keys(validation).length > 0) return

    setSubmitting(true)
    try {
      const res = await fetch('/api/mfa-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          companyName: form.companyName.trim(),
          email: form.email.trim(),
          responseTime: form.responseTime.value,
          whyReset: form.whyReset.value,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success('Request received. Our on-call team will reach out shortly.')
      setForm({ name: '', companyName: '', email: '', responseTime: null, whyReset: null })
    } catch (err) {
      toast.error('Something went wrong submitting your request. Please email support@caylent.com.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Page>
      <Helmet>
        <title>MFA Reset Request | Caylent</title>
      </Helmet>
      <Logo src={logo} alt="Caylent" />
      <Box>
        <Heading>Request an MFA Reset</Heading>
        <Lead>
          Locked out of your account? Tell us who you are and how quickly you need help, and our
          on-call team will reach out.
        </Lead>
        <form onSubmit={handleSubmit} noValidate>
          <Field>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && <ErrorMsg id="name-error" role="alert">{errors.name}</ErrorMsg>}
          </Field>

          <Field>
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              name="companyName"
              autoComplete="organization"
              value={form.companyName}
              onChange={handleChange}
              aria-invalid={!!errors.companyName}
              aria-describedby={errors.companyName ? 'companyName-error' : undefined}
            />
            {errors.companyName && (
              <ErrorMsg id="companyName-error" role="alert">{errors.companyName}</ErrorMsg>
            )}
          </Field>

          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && <ErrorMsg id="email-error" role="alert">{errors.email}</ErrorMsg>}
          </Field>

          <Field>
            <Label htmlFor="responseTime">What response time is required?</Label>
            <Select
              inputId="responseTime"
              options={RESPONSE_TIME_OPTIONS}
              value={form.responseTime}
              onChange={handleResponseTimeChange}
              placeholder="Select response time..."
              styles={selectStyles}
              aria-invalid={!!errors.responseTime}
            />
            {errors.responseTime && (
              <ErrorMsg role="alert">{errors.responseTime}</ErrorMsg>
            )}
          </Field>

          <Field>
            <Label htmlFor="whyReset">Why do you need a reset?</Label>
            <Select
              inputId="whyReset"
              options={WHY_RESET_OPTIONS}
              value={form.whyReset}
              onChange={handleWhyResetChange}
              placeholder="Select a reason..."
              styles={selectStyles}
              aria-invalid={!!errors.whyReset}
            />
            {errors.whyReset && (
              <ErrorMsg role="alert">{errors.whyReset}</ErrorMsg>
            )}
          </Field>

          <Actions>
            <Button type="submit" disabled={submitting} isSubmitting={submitting}>
              Submit Request
            </Button>
          </Actions>
        </form>
      </Box>
    </Page>
  )
}

export default MfaResetForm
